import cn from 'classnames';
import Footer from 'components/Footer';
import Header from 'components/Header';
import HelpCenterHeader from 'components/Header/HelpCenterHeader';
import { P } from 'components/base';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Cookies, CookiesProvider, withCookies } from 'react-cookie';
import Helmet from 'react-helmet';
import { siteNotificationService } from 'services/site-notification.service';
import 'styles/_global.scss';
import 'styles/components/_components.scss';
import 'styles/reset.css';
import WarningIcon from 'svgs/icons/exclamation-circle.svg';
import SMEHeader from 'templates/SMETpl/SMEHeader';
import { SiteNotificationStore, useSiteNotfication } from '../../states/site-notification.state';
import {
  alertCard,
  alertIcon,
  alertSection,
  alertText,
  blue,
  contentC,
  hasProductNavC,
  light,
  transparentBgC
} from './layout.module.scss';

const checkIsIe = () => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');
  // eslint-disable-next-line no-useless-escape
  return msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./);
};

const Layout = props => {
  const {
    children,
    location,
    transparentBg,
    productNav,
    helpCenter,
    customizedHeader,
    data: {
      site: {
        siteMetadata: { env, siteUrl },
      },
      siteJson: {
        meta: { favicon, ogImage, keyword },
        company: { logo },
        header
      },
    },
  } = props;
  const url = `${siteUrl}${location.pathname}`;
  const theme = helpCenter ? 'blue' : 'light';

  const { headerNotification, setSiteNotifications } = useSiteNotfication();
  useEffect(() => {
    siteNotificationService
      .getOfficialNotifications()
      .then(setSiteNotifications)
      .catch(err => {
        console.error(err);
      });
  }, []);

  const [isIE, setIsIE] = useState(false);
  useEffect(() => {
    setIsIE(checkIsIe());
  }, []);

  return (
    <CookiesProvider>
      <div>
        <Helmet>
          <html
            lang="zh-TW"
            className={cn({ [blue]: theme === 'blue', [light]: theme === 'light' })}
          />
          <meta name="keywords" content={keyword} />
          <link rel="icon" href={favicon.publicURL} type="image/png" />
          <meta name="copyright" content="newsleopard" />
          <link rel="canonical" href={url} />
          <meta name="url" content={url} />

          <meta property="fb:app_id" content="684807045761009" />
          <meta property="og:image" content={ogImage.publicURL} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="newsleopard" />
          <meta property="og:url" content={url} />
          <meta name="twitter:card" content="summary" />
          <meta name="facebook-domain-verification" content="grbx7b1j2nbn1l240mmuv88ozfijlv" />

          {env === 'production' && (
            /* GTM, {Live} Environment Newsleopard Offcial + App */
            <script>
              {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl+ '&gtm_auth=zfL07enkow83_RqcmDwRdA&gtm_preview=env-1&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-MLTQDHK');(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl+ '&gtm_auth=PutM5NilnJv03TQdd1RI8w&gtm_preview=env-2&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','GTM-MTPTX66');`}
            </script>
          )}
          
          {/* Start of HubSpot Embed Code */}
          <script type="text/javascript" id="hs-script-loader" async defer src="//js.hs-scripts.com/22142902.js"></script>
          {/* End of HubSpot Embed Code */}
        </Helmet>
        {/* header START */}
        {isIE && (
          <section className={alertSection}>
            <div className={alertCard}>
              <WarningIcon className={alertIcon} />
              <P className={alertText}>
                本網站不支援 IE 瀏覽器，請使用&nbsp;
                <a href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer">
                  Chrome
                </a>
                &nbsp; 或其他瀏覽器，以完整體驗網站各項功能。
              </P>
            </div>
          </section>
        )}
        {customizedHeader ? (
          <>
            {customizedHeader === 'sme' && <SMEHeader logoUrl={logo.publicURL} />}
            {customizedHeader === 'helpCenter' && (
              <HelpCenterHeader logoUrl={logo.publicURL} productName={helpCenter.productName} />
            )}
          </>
        ) : (
          <Header
            location={location}
            logoUrl={logo}
            transparentBg={transparentBg}
            productNav={productNav}
            notification={headerNotification}
            data={header}
          />
        )}
        {/* header END */}
        <main
          className={cn(contentC, {
            [transparentBgC]: transparentBg,
            [hasProductNavC]: productNav,
          })}
        >
          {children}
        </main>
        <Footer />
      </div>
    </CookiesProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object,
  data: PropTypes.shape({
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        env: PropTypes.string.isRequired,
        siteUrl: PropTypes.string.isRequired,
      }),
    }),
    siteJson: PropTypes.shape({
      meta: PropTypes.shape({
        favicon: PropTypes.shape({
          publicURL: PropTypes.string.isRequired,
        }),
        ogImage: PropTypes.shape({
          publicURL: PropTypes.string.isRequired,
        }),
        keyword: PropTypes.string,
      }),
      company: PropTypes.shape({
        logo: PropTypes.shape({
          publicURL: PropTypes.string.isRequired,
        }),
      }),
      header: PropTypes.object.isRequired,
    }),
  }),
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  transparentBg: PropTypes.bool,
  productNav: PropTypes.shape({
    productName: PropTypes.oneOf(['newsleopard', 'surenotify']),
  }),
  helpCenter: PropTypes.shape({
    productName: PropTypes.oneOf(['newsleopard', 'surenotify']),
  }),
  customizedHeader: PropTypes.oneOf(['sme', 'helpCenter']),
};
Layout.defaultProps = {
  location: {
    pathname: '/',
  },
  transparentBg: false,
  productNav: undefined,
  helpCenter: undefined,
};

const comp = props => {
  const data = useStaticQuery(graphql`
    query LayoutQuery {
      site {
        siteMetadata {
          env
          siteUrl
        }
      }
      siteJson {
        meta {
          favicon {
            publicURL
          }
          ogImage {
            publicURL
          }
          keyword
        }
        company {
          logo {
            publicURL
          }
        }
        header {
          product {
            data {
              title
              list {
                heading
                link
                openNew
                image {
                  publicURL
                }
                desc
              }
            }
            dataSide{
              title
              list {
                heading
                link
                openNew
                image {
                  publicURL
                }
                desc
              }
            }
          }
          customers {
            data {
              title
              list {
                heading
                link
                openNew
                image {
                  publicURL
                }
                desc
              }
            }
            dataSide{
              title
              list {
                heading
                link
                openNew
                image {
                  publicURL
                }
                desc
              }
            }
          }
          resource {
            data {
              title
              list {
                heading
                link
                openNew
                image {
                  publicURL
                }
                desc
              }
            }
            dataSide{
              title
              list {
                heading
                link
                openNew
                image {
                  publicURL
                }
                desc
              }
            }
          }
        }
      }
    }
  `);
  return (
    <SiteNotificationStore>
      <Layout data={data} {...props} />
    </SiteNotificationStore>
  );
};

export default withCookies(comp);
